import { ResponseOverviewLiveUsers } from '@trace/types';
import { DateTime, Duration } from 'luxon';
import { useContext, useEffect, useState } from 'react';
import { useQuery, UseQueryResult } from 'react-query';

import { AuthContextTrace } from '../Authenticate/AuthenticateTrace';
import { callCloud } from '../../fetch/fetch';

interface Payload {
  data?: number[];
  labels: string[];
}

interface HookPayload extends Payload {
  isFetched: boolean;
}

export type HookResult = Pick<UseQueryResult, 'isError' | 'isFetching' | 'refetch'> & HookPayload;

const useOverviewLiveUsers = (): HookResult => {
  const { token: traceToken } = useContext(AuthContextTrace);
  const [payload, setPayload] = useState<Payload>({ labels: [] });
  const { data, isError, isFetching, isSuccess, refetch } = useQuery<ResponseOverviewLiveUsers>(
    ['overviewLiveUsers', DateTime.utc().startOf('minute').toISO()],
    () =>
      callCloud<ResponseOverviewLiveUsers>(
        'GET',
        '/trace/api/metrics/live_users',
        traceToken || '',
      ),
    { refetchInterval: 1000 * 10 },
  );

  useEffect(() => {
    if (data) {
      const { duration, metrics, start, step } = data;
      const dur = Duration.fromISO(duration);
      const ticks = dur.get(step);
      const tickDates = Array.from({ length: ticks }).map<DateTime>((_, index) =>
        DateTime.fromISO(start, { zone: 'utc' }).plus({ [step]: index }),
      );

      setPayload({
        data: metrics,
        labels: tickDates.map((dt) => dt.toLocal().toFormat('T')),
      });
    }
  }, [data]);

  return {
    isError,
    isFetched: isSuccess,
    isFetching,
    refetch,
    ...payload,
  };
};

export default useOverviewLiveUsers;
